import React, { useState } from 'react';
import { useToast } from '../hooks/use-toast';
import { contactAPI } from '../api';
import { Mail, Phone, MapPin, MessageCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Reveal } from './Reveal';

const ContactSection = () => {
  const { toast } = useToast();
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '', phone: '', email: '', message: '', acceptTerms: false, acceptMarketing: false,
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.acceptTerms) {
      toast({
        title: 'Error',
        description: language === 'es' ? 'Debe aceptar los términos y condiciones' : 'You must accept the terms and conditions',
        variant: 'destructive',
      });
      return;
    }
    setSubmitting(true);
    try {
      await contactAPI.submit(formData);
      toast({
        title: language === 'es' ? '¡Mensaje enviado!' : 'Message sent!',
        description: language === 'es' ? 'Nos pondremos en contacto pronto.' : 'We will contact you soon.',
      });
      setFormData({ name: '', phone: '', email: '', message: '', acceptTerms: false, acceptMarketing: false });
    } catch (error) {
      console.error('Error submitting contact:', error);
      toast({
        title: 'Error',
        description: language === 'es' ? 'Error al enviar el mensaje. Inténtelo de nuevo.' : 'Error sending message. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = 'w-full px-0 py-3 bg-transparent border-b border-white/15 text-white focus:border-brand focus:outline-none transition-colors placeholder:text-white/30 disabled:opacity-50';

  return (
    <section id="contacto" className="bg-surface py-24 md:py-32 relative overflow-hidden">
      <div className="absolute top-1/3 left-0 w-[34rem] h-[34rem] bg-brand/8 rounded-full blur-[140px] pointer-events-none" />

      <div className="container mx-auto px-6 lg:px-10 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-start">
          {/* Left: heading + info */}
          <Reveal>
            <p className="overline mb-5">{t('contact.sectionTitle')}</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light text-white mb-6 tracking-tight leading-tight">
              {t('contact.title1')}
              <span className="block text-gradient-blue font-normal">{t('contact.title2')}</span>
            </h2>
            <p className="text-white/55 text-lg mb-12 max-w-md leading-relaxed">{t('contact.subtitle')}</p>

            <div className="space-y-7">
              <div className="flex items-start gap-4">
                <div className="text-brand-light mt-1"><Phone size={22} /></div>
                <div>
                  <p className="text-white/40 text-sm mb-1">{t('contact.info.phone')}</p>
                  <div className="flex items-center gap-3">
                    <a href="tel:+34607574368" className="text-white hover:text-brand-light transition-colors">+34 607 57 43 68</a>
                    <a href="https://wa.me/34607574368" target="_blank" rel="noopener noreferrer" className="text-green-500 hover:text-green-400 transition-colors" title="WhatsApp">
                      <MessageCircle size={18} />
                    </a>
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="text-brand-light mt-1"><Mail size={22} /></div>
                <div>
                  <p className="text-white/40 text-sm mb-1">{t('contact.info.email')}</p>
                  <a href="mailto:admin@domodomotics.com" className="text-white hover:text-brand-light transition-colors">admin@domodomotics.com</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="text-brand-light mt-1"><MapPin size={22} /></div>
                <div>
                  <p className="text-white/40 text-sm mb-1">{t('contact.info.address')}</p>
                  <p className="text-white">Calle Newton Nº22<br />Estepona, España</p>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Right: form */}
          <Reveal delay={0.15} className="glass-card rounded-2xl p-8 md:p-10">
            <form onSubmit={handleSubmit} className="space-y-7">
              <div>
                <label className="block text-xs uppercase tracking-wider text-white/40 mb-2">{t('contact.form.name')} *</label>
                <input type="text" name="name" value={formData.name} onChange={handleChange} required disabled={submitting} className={inputClass} />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-white/40 mb-2">{t('contact.form.phone')} *</label>
                <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required disabled={submitting} className={inputClass} />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-white/40 mb-2">{t('contact.form.email')} *</label>
                <input type="email" name="email" value={formData.email} onChange={handleChange} required disabled={submitting} className={inputClass} />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-white/40 mb-2">{t('contact.form.message')} *</label>
                <textarea name="message" value={formData.message} onChange={handleChange} required disabled={submitting} rows={4} placeholder={t('contact.form.messagePlaceholder')} className={`${inputClass} resize-none`} />
              </div>

              <div className="space-y-3 pt-2">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" name="acceptTerms" checked={formData.acceptTerms} onChange={handleChange} disabled={submitting} className="mt-1 w-4 h-4 accent-brand disabled:opacity-50" />
                  <span className="text-sm text-white/50">{t('contact.form.acceptTerms')} *</span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" name="acceptMarketing" checked={formData.acceptMarketing} onChange={handleChange} disabled={submitting} className="mt-1 w-4 h-4 accent-brand disabled:opacity-50" />
                  <span className="text-sm text-white/50">{t('contact.form.acceptMarketing')}</span>
                </label>
              </div>

              <button type="submit" disabled={submitting} data-testid="contact-submit" className="btn-primary w-full px-8 py-4 rounded-sm text-sm uppercase tracking-wider font-medium disabled:opacity-50 disabled:cursor-not-allowed">
                {submitting ? t('contact.form.sending') : t('contact.form.submit')}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
